import type { MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import type { Club } from "@/lib/api";
import { primaryLink } from "@/lib/meeting";
import { SECTIONS } from "@/lib/sections";

interface MastheadProps {
  readonly activeId?: string;
  /** A sub-page has no in-page sections, so it shows a back link instead. */
  readonly variant?: "home" | "page";
  /**
   * Supplies the corner link. Taken from the club document rather than
   * hardcoded, so an officer can move the invite without a deploy — and passed
   * in rather than fetched here, since every page already loads it for the
   * footer and a second request would be for nothing.
   */
  readonly club?: Club | null;
}

const NAV_SECTIONS = SECTIONS.filter((section) => section.id !== "top");

export function Masthead({ activeId, variant = "home", club = null }: MastheadProps) {
  // The club's primary link, which is the Discord invite.
  const joinLink = primaryLink(club);
  const navigate = useNavigate();
  const location = useLocation();

  // React Router stamps the first entry of a session with the key "default", so
  // this is the difference between "you arrived here from somewhere on the site"
  // and "you opened this address cold". Going back from a cold load would walk
  // out of the site entirely, which is not what a link called Back to site
  // should do — so that case falls through to the front page.
  const canGoBack = location.key !== "default";

  /**
   * Left-click goes back; everything else is left alone.
   *
   * The element stays an anchor pointing at `/` so middle-click, cmd-click and
   * "open in new tab" still do the obvious thing, and so it works before the
   * JavaScript has run. Only a plain primary click is intercepted.
   */
  const goBack = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!canGoBack) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    navigate(-1);
  };

  return (
    <header
      className="sticky top-0 z-30 border-b-2 border-ink bg-field/95 backdrop-blur-[2px]
        xl:pl-[var(--rail-width)]"
    >
      <div className="flex items-center gap-4 px-4 py-3 sm:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <img
            src="/logo.webp"
            alt=""
            width={34}
            height={34}
            className="h-[34px] w-[34px] border-2 border-ink object-cover"
          />
          <span className="display text-[0.9375rem] leading-none sm:text-lg">
            Tetris At UCI
          </span>
        </Link>

        {variant === "home" ? (
          <nav aria-label="Primary" className="ml-auto hidden items-center gap-1 lg:flex">
            {NAV_SECTIONS.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                aria-current={activeId === section.id ? "page" : undefined}
                className={`meta px-2.5 py-1.5 transition-colors duration-100
                  ${
                    activeId === section.id
                      ? "bg-ink text-field"
                      : "hover:bg-ink hover:text-field"
                  }`}
              >
                {section.short}
              </a>
            ))}
          </nav>
        ) : (
          <Link
            to="/"
            onClick={goBack}
            className="meta ml-auto px-2.5 py-1.5 hover:bg-ink hover:text-field"
          >
            {canGoBack ? "← Back" : "← Back to site"}
          </Link>
        )}

        {/* Shown on every page: unlike the board, an invite is never redundant.
            Hidden entirely when the club has no link configured — a corner
            button that goes nowhere is worse than no button. */}
        {joinLink ? (
          <a
            href={joinLink.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn ml-auto hidden shrink-0 px-3 py-2 text-xs md:inline-flex lg:ml-3"
          >
            {joinLink.label}
          </a>
        ) : null}
      </div>
    </header>
  );
}
